



















































































































































































































































































































































































































import { Component, Vue, Watch } from 'vue-property-decorator';
import { Action, State } from 'vuex-class';
import { get as _get } from 'lodash';
import Container from '@/components/Container.vue';
import FHIRConfirmModal from './components/FHIRConfirmModal.vue';
import BaseLoading from '@/components/base/BaseLoading.vue';
import { isTruthy } from '@/jbi-shared/util/watcher.vue-decorator';
import { isEmail } from '@/jbi-shared/util/validate-email-domains.util';
import { RootState } from '@/store/store';
import { ApiState } from '@/store/types/general.types';
import { ToastProgrammatic as Toast } from 'buefy';
import {
  FHIRDefaultParameter,
  FHIRDownloadSettings
} from '@/store/modules/fhir/types/fhir.types';
import {
  CreateFHIRClientParameterPermission,
  CreateFHIRClientPayload
} from './types/admin-fhir-access-management.types';

interface SearchParamState {
  id: number;
  displayName: string;
  key: string;
  type: string;
  isEnabled: boolean;
  defaultAccessChosen: 'all_clients' | 'mixed' | 'configured';
  maxCardinality: number | undefined;
  defaultCardinality: number | undefined;
  isCardinalityFixed: boolean;
  selectedCardinality: number;
  selectedRadioValue: number | undefined;
  listValues: Array<{
    id: number;
    optionLabel: string;
    accessChosen: 'all_clients' | 'configured';
    isSelected: boolean;
  }>;
}

interface DownloadFormat {
  formatTypeId: number;
  formatType: string;
  isEnabled: boolean;
}

const WILDCARD_DOMAIN_REGEX = /^\*\.[a-zA-Z0-9-]+\.[a-zA-Z]{2,}$/;

function toDisplayName(key: string): string {
  return key
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

@Component({
  components: { Container, BaseLoading }
})
export default class FHIRAddClient extends Vue {
  public form = {
    name: '',
    email: '',
    domain: '',
    publicKey: '',
    isSearchActive: true,
    isDownloadActive: true
  };

  public searchParamStates: SearchParamState[] = [];
  public downloadFormats: DownloadFormat[] = [];
  public maxDocMode: 'set-limit' | 'unlimited' = 'set-limit';
  public downloadLimit = 1;

  public nameTouched = false;
  public emailTouched = false;
  public domainTouched = false;

  @Action('fhir/getDefaultParameters')
  getDefaultParameters!: () => void;

  @Action('fhir/getDefaultDownloadPermissions')
  getDefaultDownloadPermissions!: () => void;

  @Action('fhir/createFhirClient')
  createFhirClient!: (payload: CreateFHIRClientPayload) => void;

  @State(({ fhir }: RootState) => fhir.defaultParameters)
  defaultParameters!: FHIRDefaultParameter[] | undefined;

  @State(({ fhir }: RootState) => fhir.downloadSettings)
  downloadSettings!: FHIRDownloadSettings | undefined;

  @State(({ fhir }: RootState) => fhir.apiState.getDefaultParameters.loading)
  isLoadingParams!: boolean;

  @State(
    ({ fhir }: RootState) => fhir.apiState.getDefaultDownloadPermissions.loading
  )
  isLoadingDownload!: boolean;

  @State(({ fhir }: RootState) => fhir.apiState.createFhirClient)
  createFhirClientState!: ApiState;

  get isLoadingDefaults(): boolean {
    return (
      (this.isLoadingParams && !this.defaultParameters) ||
      (this.isLoadingDownload && !this.downloadSettings)
    );
  }

  get isSaving(): boolean {
    return this.createFhirClientState?.loading ?? false;
  }

  get nameError(): string {
    if (!this.nameTouched) return '';
    return this.form.name.trim() ? '' : 'Client name is required.';
  }

  get emailError(): string {
    if (!this.emailTouched) return '';
    if (!this.form.email.trim()) return 'Email is required.';
    return isEmail(this.form.email) ? '' : 'Invalid email format.';
  }

  get domainError(): string {
    if (!this.domainTouched) return '';
    if (!this.form.domain.trim()) return 'Domain restriction is required.';
    return WILDCARD_DOMAIN_REGEX.test(this.form.domain.trim())
      ? ''
      : 'Domain must be in the format *.example.com.';
  }

  get bothEndpointsDisabled(): boolean {
    return !this.form.isSearchActive && !this.form.isDownloadActive;
  }

  public isChecklistEmpty(param: SearchParamState): boolean {
    return (
      param.isEnabled &&
      param.type === 'checklist' &&
      !param.listValues.some((lv) => lv.isSelected)
    );
  }

  get isFormValid(): boolean {
    return !!(
      this.form.name.trim() &&
      this.form.email.trim() &&
      isEmail(this.form.email) &&
      this.form.domain.trim() &&
      WILDCARD_DOMAIN_REGEX.test(this.form.domain.trim()) &&
      this.form.publicKey.trim() &&
      !this.bothEndpointsDisabled &&
      !this.searchParamStates.some((p) => this.isChecklistEmpty(p))
    );
  }

  public mounted() {
    this.getDefaultParameters();
    this.getDefaultDownloadPermissions();
  }

  @Watch('defaultParameters')
  public onDefaultParametersLoaded(params: FHIRDefaultParameter[] | undefined) {
    if (!params) return;
    this.searchParamStates = params.map((p) => {
      const isEnabled = p.accessChosen !== 'configured';
      const listValues = (p.listValues ?? []).map((lv) => ({
        id: lv.id,
        optionLabel: lv.optionLabel,
        accessChosen: lv.accessChosen as 'all_clients' | 'configured',
        isSelected: lv.accessChosen === 'all_clients'
      }));

      const defaultRadio = listValues.find(
        (lv) => lv.accessChosen === 'all_clients'
      );

      return {
        id: p.id,
        displayName: toDisplayName(p.name),
        key: p.name,
        type: p.type,
        isEnabled,
        defaultAccessChosen: p.accessChosen as
          | 'all_clients'
          | 'mixed'
          | 'configured',
        maxCardinality: p.maxCardinality > 0 ? p.maxCardinality : undefined,
        defaultCardinality:
          p.defaultCardinality > 0 ? p.defaultCardinality : undefined,
        isCardinalityFixed: p.isCardinalityFixed,
        selectedCardinality: p.defaultCardinality || 1,
        selectedRadioValue: defaultRadio?.id ?? listValues[0]?.id,
        listValues
      };
    });
  }

  @Watch('downloadSettings')
  public onDownloadSettingsLoaded(settings: FHIRDownloadSettings | undefined) {
    if (!settings) return;
    this.downloadFormats = settings.formats.map((f) => ({
      formatTypeId: f.formatTypeId,
      formatType: f.formatType,
      isEnabled: f.isEnabled
    }));
    this.maxDocMode = settings.isUnlimited ? 'unlimited' : 'set-limit';
    this.downloadLimit = settings.downloadLimit ?? 1;
  }

  public accessLabel(access: string): string {
    const map: Record<string, string> = {
      all_clients: 'ALL_CLIENTS',
      mixed: 'MIXED',
      configured: 'CONFIGURED'
    };
    return map[access] ?? access.toUpperCase();
  }

  public handleExit() {
    this.$buefy.modal.open({
      parent: this,
      component: FHIRConfirmModal,
      hasModalCard: true,
      trapFocus: true,
      props: {
        modalTitle: 'Discard New Client?',
        modalContent:
          "Your changes haven't been saved. All entered details will be lost.",
        confirmBtnText: 'Discard',
        cancelBtnText: 'Continue Editing'
      },
      events: {
        confirm: () => this.exitToList()
      }
    });
  }

  public handleCancel() {
    this.handleExit();
  }

  private exitToList() {
    this.$router.push({
      name: 'third-party-management',
      query: { tab: 'FHIR APIs' }
    });
  }

  public handleSubmit() {
    this.nameTouched = true;
    this.emailTouched = true;
    this.domainTouched = true;
    if (!this.isFormValid) return;

    this.$buefy.modal.open({
      parent: this,
      component: FHIRConfirmModal,
      hasModalCard: true,
      trapFocus: true,
      props: {
        modalTitle: 'Add Client?',
        modalContent:
          'Please review all details before confirming. This action will create the client and activate API access.',
        confirmBtnText: 'Confirm',
        cancelBtnText: 'Cancel'
      },
      events: {
        confirm: () => this.submitCreateClient()
      }
    });
  }

  private async submitCreateClient() {
    const parameters: CreateFHIRClientParameterPermission[] = this.searchParamStates.map(
      (p) => {
        const sp: CreateFHIRClientParameterPermission = {
          parameterId: p.id,
          isActive: p.isEnabled,
          cardinality: p.maxCardinality ? p.selectedCardinality : 0,
          accessChosen:
            p.defaultAccessChosen === 'mixed'
              ? 'configured'
              : p.defaultAccessChosen
        };
        if (p.isEnabled) {
          if (p.type === 'radioList' && p.selectedRadioValue !== undefined) {
            sp.listValueIds = [p.selectedRadioValue];
          }
          if (p.type === 'checklist') {
            sp.listValueIds = p.listValues
              .filter((lv) => lv.isSelected)
              .map((lv) => lv.id);
          }
        }
        return sp;
      }
    );

    const payload: CreateFHIRClientPayload = {
      name: this.form.name.trim(),
      email: this.form.email.trim(),
      domain: this.form.domain.trim(),
      publicKey: this.form.publicKey.trim(),
      isSearchActive: this.form.isSearchActive,
      isDownloadActive: this.form.isDownloadActive,
      parameters,
      download: {
        isUnlimited: this.maxDocMode === 'unlimited',
        downloadLimit:
          this.maxDocMode === 'set-limit' ? this.downloadLimit : undefined,
        formatIds: this.downloadFormats
          .filter((f) => f.isEnabled)
          .map((f) => f.formatTypeId)
      }
    };

    await this.createFhirClient(payload);
  }

  @Watch('createFhirClientState.success')
  @isTruthy
  public onCreateSuccess() {
    Toast.open({
      message: 'FHIR client created successfully.',
      position: 'is-top',
      type: 'is-success'
    });
    this.$router.push({
      name: 'third-party-management',
      query: { tab: 'FHIR APIs' }
    });
  }

  @Watch('createFhirClientState.error')
  @isTruthy
  public onCreateError() {
    Toast.open({
      message: _get(
        this.createFhirClientState.error,
        'response.data.message',
        'Failed to create FHIR client.'
      ),
      position: 'is-top',
      type: 'is-danger'
    });
  }
}
